import { t } from '@lingui/core/macro'
import isUndefined from 'lodash/isUndefined'
import URI from 'urijs'
import uuid from 'uuid/v4'
import { LinkConversionData } from './link.types'

const VALIDATION_CACHE = {}

/**
 *
 * @param link
 * @param linkGovernance
 * @returns {boolean}
 */
export function isLinkValid(link, linkGovernance) {
  // if the user entered URI cannot be parsed then we cannot tell if it is valid, so we will default to invalid.
  const userEnteredURI = cleanLink(link)
  if (!userEnteredURI.parsable) {
    return false
  }

  // if no restrictions, it's always valid.
  if (
    !link ||
    !linkGovernance?.domain_restrictions ||
    linkGovernance.domain_restrictions.length === 0
  ) {
    return true
  }

  // if no uuid, assign one
  if (!linkGovernance._uuid) {
    linkGovernance._uuid = uuid()
  }

  // check the cache for the uuid
  const cacheKey = `${link}__${linkGovernance._uuid}`
  if (!isUndefined(VALIDATION_CACHE[cacheKey])) {
    return VALIDATION_CACHE[cacheKey]
  }

  const parsedDomainRestrictions = linkGovernance.domain_restrictions.map(restriction =>
    cleanLink(restriction.domain),
  )

  // compare the entered uri with the restrictions
  const matches = parsedDomainRestrictions.filter(restriction =>
    linkMatchesRestriction(userEnteredURI, restriction),
  )

  // populate the cache
  const result = matches.length > 0
  VALIDATION_CACHE[cacheKey] = result

  // return the result
  return result
}

/**
 *
 * @param link
 */
export function cleanLink(link: string | null): LinkConversionData {
  // trim the link string. copy/paste adds a \n character for some reason
  const trimmedLink = link?.trim()

  if (!trimmedLink) {
    return {
      message: t`No URI was found`,
      URI: URI(''),
      parsable: true,
    }
  }

  const parsed = URI(trimmedLink)
  if (parsed.protocol()) {
    return {
      message: t`Parsed Successfully`,
      URI: parsed.normalize(),
      parsable: true,
    }
  }

  // The parsing did not find a protocol so we add it manually here
  try {
    const parsedWithProtocol = URI(`https://${trimmedLink}`).normalize()
    return {
      message: t`Parsed Successfully`,
      URI: parsedWithProtocol,
      parsable: true,
    }
  } catch (e: unknown) {
    const errorMessage = e instanceof Error ? e.message : 'Unknown error'
    return {
      message: t`The was an error parsing the URI: ${errorMessage}`,
      URI: URI(''),
      parsable: false,
    }
  }
}

/*
---------------------------------
Internal Functions
---------------------------------
 */
function linkMatchesRestriction(toCheck, restriction) {
  if (toCheck.parsable) {
    if (toCheck.URI.protocol() !== restriction.URI.protocol()) {
      return false
    }

    if (restriction.URI.subdomain() && restriction.URI.subdomain() !== toCheck.URI.subdomain()) {
      return false
    }

    if (restriction.URI.domain() !== toCheck.URI.domain()) {
      return false
    }

    if (restriction.URI.path() && !toCheck.URI.path().includes(restriction.URI.path())) {
      return false
    }
  } else {
    return false
  }

  return true
}
